import React, { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

type ModalSize = 'sm' | 'md' | 'lg';

interface AppModalProps {
    controlFunc?: (flag?: boolean) => void;
    Children: React.ComponentType<any>;
    childrenProps: object;
    title: string;
    acceptFunction?: () => void;
    size?: ModalSize;
    disableHide?: boolean;
    disableOutsideClick?: boolean;
}

const ANIMATION_MS = 220;

const AppModalGeneral: React.FC<AppModalProps> = ({
    controlFunc,
    Children,
    childrenProps,
    title,
    size = 'sm',
    disableHide,
    disableOutsideClick,
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const closeFunc = (): void => {
        if (disableHide || !controlFunc) return;
        setIsOpen(false);
        setTimeout(() => {
            controlFunc();
        }, ANIMATION_MS);
    };

    useEffect(() => {
        setIsOpen(true);
    }, []);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                closeFunc();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disableHide, controlFunc]);

    const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (disableOutsideClick) return;
        // Only close when the user pressed directly on the overlay backdrop,
        // not on the sheet or any of its descendants (selectors, dropdowns…).
        if (e.target === e.currentTarget) {
            closeFunc();
        }
    };

    return (
        <Overlay $isOpen={isOpen} onMouseDown={handleOverlayMouseDown}>
            <Sheet $isOpen={isOpen} $size={size}>
                <Header>
                    <HeaderTitle>{title}</HeaderTitle>
                    {controlFunc && !disableHide && (
                        <CloseBtn type='button' onClick={closeFunc} aria-label='close'>
                            <CloseGlyph />
                        </CloseBtn>
                    )}
                </Header>
                <Body>
                    <Children {...childrenProps} closeFunc={closeFunc} />
                </Body>
            </Sheet>
        </Overlay>
    );
};

const AppModal = React.memo(AppModalGeneral);

export default AppModal;

const CloseGlyph = () => (
    <svg
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth={2}
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden
    >
        <line x1='6' y1='6' x2='18' y2='18' />
        <line x1='18' y1='6' x2='6' y2='18' />
    </svg>
);

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${({ $isOpen }) => ($isOpen ? 'rgba(15, 23, 42, 0.45)' : 'rgba(15, 23, 42, 0)')};
    transition: background ${ANIMATION_MS}ms ease;
    z-index: 100;
    padding: 1rem;
    animation: ${fadeIn} ${ANIMATION_MS}ms ease;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: 0.75rem;
        /* The on-screen keyboard shrinks the visible area; instead of squashing the Sheet, let the
           overlay scroll. Centering moves to the Sheet's auto margins so a taller Sheet is never
           cut off at the top (align-items: center would overflow both ways). */
        overflow-y: auto;
        align-items: flex-start;
    }
`;

const sizeMap: Record<ModalSize, ReturnType<typeof css>> = {
    sm: css`
        width: 420px;
        max-width: 92vw;
    `,
    md: css`
        width: min(720px, 90vw);
    `,
    lg: css`
        width: min(960px, 92vw);
    `,
};

const Sheet = styled.div<{ $isOpen: boolean; $size: ModalSize }>`
    position: relative;
    ${({ $size }) => sizeMap[$size]}
    /* Sheet always fits inside the Overlay viewport (inset:0 + padding) —
       avoids 100vh quirks on mobile browsers with dynamic toolbars. */
    height: auto;
    max-height: 100%;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: 1.25rem;
    box-shadow: ${({ theme }) => theme.shadows.card};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    direction: rtl;
    /* 'none' (not translateY(0)) when open: any transform makes the Sheet the containing block for
       position:fixed descendants, so fixed poppers (date pickers) got clipped by overflow: hidden. */
    transform: ${({ $isOpen }) => ($isOpen ? 'none' : 'translateY(16px)')};
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    transition:
        transform ${ANIMATION_MS}ms ease,
        opacity ${ANIMATION_MS}ms ease;

    @media (min-width: 541px) {
        /* Cap desktop height while keeping flex-driven sizing for content. */
        max-height: min(720px, 100%);
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 100%;
        max-width: 100%;
        border-radius: 1rem;
        margin-block: auto;
        flex-shrink: 0;
        /* svh ignores the on-screen keyboard, so the Sheet keeps its height (footer stays below the
           content) instead of following the shrinking overlay. 1.5rem = overlay padding × 2. */
        max-height: calc(100svh - 1.5rem);
    }
`;

const Header = styled.header`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.875rem 1.25rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.line};
    background: ${({ theme }) => theme.colors.surface};
`;

const HeaderTitle = styled.h2`
    margin: 0;
    flex: 1;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.xl};
    font-weight: 600;
    line-height: 1.2;
    color: ${({ theme }) => theme.colors.ink};
    text-align: start;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.xl};
    }
`;

const CloseBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${({ theme }) => theme.colors.ink3};
    cursor: pointer;
    border-radius: 0.5rem;
    transition:
        color ${({ theme }) => theme.motion.fast} ease,
        background ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1.125rem;
        height: 1.125rem;
    }

    &:hover {
        color: ${({ theme }) => theme.colors.ink};
        background: ${({ theme }) => theme.colors.line2};
    }
`;

const Body = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 1rem 1.25rem 1.25rem;
    background: ${({ theme }) => theme.colors.surface};
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        width: 0;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: 0.875rem 1rem 1rem;
    }
`;
